import { Component, Vue, Prop, Provide, Emit, Watch, Inject } from 'vue-property-decorator';
import { PropOptions } from 'vue';
import CreditCardContainer from './CreditCardContainer.vue';
import InvoiceContainer from './InvoiceContainer.vue';
import AchContainer from './AchContainer.vue';
import { default as InvoiceContainerClass } from '@/components/payment-container/InvoiceContainerVue';
import { default as CreditCardContainerClass } from '@/components/payment-container/CreditCardContainerVue';
import { default as AchContainerClass } from '@/components/payment-container/AchContainerVue';
import { createPaymentProcessorOptions, PaymentProcessorOptions, PaymentContainerToken, createPaymentContainerToken, InvoiceTokenData, CreditCardTokenData, PaymentTypeEnum, AchTokenData, AutoPayOption, ApiErrorResponse, PaymentPlan } from '@/common/types/AllTypesExports';
import EventBus from '../../common/services/EventBus';
import { ApiPaymentErrorResponse } from '@/common/types/Payment/ApiPaymentErrorResponse';
import { PaymentProcessingStatus } from '@/common/types/Payment/PaymentProcessingStatus';
import ErrorAlert from '../../components/errors/ErrorAlert.vue';
import Validation from '../../common/services/ValidationService';
import { paymentsRepo } from '../../common/repositories/PaymentsRepo';
import { DtoCurrentUser, createDtoCurrentUser } from '@/common/types/user/DtoCurrentUser';
import { AxiosResponse } from 'axios';
import { customerRepo } from '../../common/repositories/CustomerRepo';
import { INoviNsPaymentGlobals } from '../../common/types/Payment/NoviNsPaymentGlobals';

declare let noviNs: INoviNsPaymentGlobals;

@Component({
    name: 'PaymentContainer',
    components: {
        CreditCardContainer,
        InvoiceContainer,
        AchContainer,
        ErrorAlert
    }
})
export default class PaymentContainer extends Vue {
    @Prop({ required: true, default: () => createPaymentProcessorOptions() } as PropOptions<PaymentProcessorOptions>)
    processorOptions!: PaymentProcessorOptions;

    @Prop({ required: false, default: false })
    disabled!: boolean;

    @Prop({ required: false, default: false })
    autoPayRequired!: boolean;

    @Prop({ required: false, default: () => 0 })
    duesPayerId!: number;

    @Prop({ required: false, default: undefined })
    initialErrorMessage: string | undefined;

    @Prop({ required: false, default: () => 0 })
    proposedDonateTransactionFeeAmount: number = 0;

    @Prop({ required: false, default: () => 0 })
    eligibleForPaymentPlanAmount: number = 0;

    @Prop({ required: false, default: () => [] })
    eligibleItemsForPaymentPlan: [] = [];

    @Prop({ required: true, default: false })
    setMemberTypeDefaultBillableParty: boolean | undefined;

    @Provide('user')
    currentUser: DtoCurrentUser = createDtoCurrentUser();

    isDonatingTransactionFee: boolean = false;

    @Inject('validator')
    $validator: any;

    /* This is used to determine if a user has logged in during the course of MemberRegistration.
    Normally we can check a user's logged in status from the PaymentContainer, but when the user is coming from MemberRegistration,
    PaymentContainer is a grandchild of MemberRegistration which is the component that is determining and updating the logged in status. 
    However, the PaymentContainer is not always a nested component of MemberRegistration, so this property is optional */
    @Inject('userLoggedInOnMemberRegistration') readonly userLoggedInGetter?: () => boolean;

    creditCardActive = false;
    invoiceActive = false;
    paymentPlansActive = false;
    achActive = false;
    paymentContainerToken: PaymentContainerToken = createPaymentContainerToken(<PaymentContainerToken>{ paymentType: this.paymentType });
    isProcessing = true;
    hasError = false;
    preventCreditCard = false;
    creditCardLocked = false;
    cardRestrictionMessage = "";
    invoiceLocked = false;
    preventAch = false;
    achLocked = false;
    errorMessage = '';
    globalErrorMessage: string = '';
    total: number | null = this.processorOptions.initialTotal;
    guestName: string = this.processorOptions.initialGuestName;
    guestCompany: string = this.processorOptions.initialGuestCompany;
    forceSaveCard = false;
    forceSaveAch = false;
    hasItemWithDisabledInvoicing = false;
    disabledInvoiceTitle = '';
    autoPayOptions: AutoPayOption[] = [];
    autoPayOptionsPulled = false;
    billablePartiesNotLoadedErrorMessage = 'Please select a Bill To party for this transaction.';
    paymentPlansNotLoadedErrorMessage = 'Please select a Payment Plan for this transaction.';
    userLoaded = false;
    isPayingSubscriptionInvoice: boolean = this.processorOptions.isPayingSubscriptionInvoice;
    paymentPlans: PaymentPlan[] = [];

    private currentPaymentType: PaymentTypeEnum = PaymentTypeEnum.creditCard;
    private localEnableInvoice: boolean = true;

    get free() {
        return this.total === null || this.total === 0;
    }

    get showTabs() {
        let numberOfTabs = 0;
        if (this.processorOptions.enableInvoice) {
            numberOfTabs++;
        }
        if (this.processorOptions.enableCreditCard) {
            numberOfTabs++;
        }
        if (this.processorOptions.enableACH) {
            numberOfTabs++;
        }

        return numberOfTabs > 1;
    }

    @Watch("processorOptions.enableACH")
    updatePaymentType() {
        if (this.processorOptions.enableACH) {
            this.toggleTabActivation(PaymentTypeEnum.ach);
        }
        else if (this.processorOptions.enableCreditCard) {
            this.toggleTabActivation(PaymentTypeEnum.creditCard);
        }
    }

    get paymentType(): PaymentTypeEnum {
        if (this.currentPaymentType)
            return this.currentPaymentType;

        if (this.invoiceActive) {
            return PaymentTypeEnum.invoice;
        }
        else if (this.achActive) {
            return PaymentTypeEnum.ach;
        }
        else {
            return PaymentTypeEnum.creditCard;
        }
    }

    set paymentType(value: PaymentTypeEnum) {
        this.currentPaymentType = value;
    }

    get isInvoiceEnabled() {
        return this.processorOptions.enableInvoice && this.localEnableInvoice;
    }

    get convenienceFee() {
        return !this.free && this.invoiceActive && !this.paymentContainerToken.invoiceTokenData.waiveInvoiceFee ? this.processorOptions.invoiceFee : 0;
    }

    get paymentPlanMinimumAmount() {
        return noviNs.paymentPlanMinimumAmount;
    }

    get isSubmitDisabled() {
        if (this.invoiceActive && this.showWarningIcon) {
            return true;
        }

        if (this.disabled)
            return true;

        if (this.isProcessing)
            return true;


        if (this.processorOptions.showAdminAchDisabledMessage && this.paymentType == PaymentTypeEnum.ach)
            return true;

        if (this.total && this.total > 0) {
            if (this.paymentType == PaymentTypeEnum.creditCard && (!this.processorOptions.enableCreditCard || this.creditCardLocked || this.cardRestrictionMessage || this.preventCreditCard))
                return true;

            if (this.paymentType == PaymentTypeEnum.ach && this.preventAch)
                return true;

            if (this.paymentType == PaymentTypeEnum.invoice && (!this.isInvoiceEnabled || this.invoiceLocked))
                return true;
        }

        if (this.total == null)
            return true;

        // This is a special check for donations inputs on the checkout that could be in error but are not being propagated to the container. 
        // To accomidate this we will do a check if there are any errors with donation inputs at this scope and lock the button if so. 
        if (this.errors.items.filter(x => x.field != null && x.field.includes("_amount")).length > 0)
            return true;

        if (this.invoiceActive && (this.hasItemWithDisabledInvoicing || this.processorOptions.invoiceWarningMessage.includes("Please purchase separately") || this.processorOptions.invoiceWarningMessage.includes("You must login")))
            return true;

        if (this.paymentType == PaymentTypeEnum.ach && this.isLoggedIn == false)
            return true;

        return false;
    }

    get processingText() {
        return this.processorOptions.submitButtonSavingText
            ? this.processorOptions.submitButtonSavingText
            : 'Processing...';
    }

    get submitButtonText() {
        return this.processorOptions.submitButtonText
            ? this.processorOptions.submitButtonText
            : 'Complete Registration';
    }

    get disabledText() {
        if (this.isProcessing)
            return this.processingText;
        else if (this.creditCardLocked || this.invoiceLocked)
            return this.submitButtonText;
        else if (this.processorOptions.disabledButtonText)
            return this.processorOptions.disabledButtonText;
        else
            return this.submitButtonText;
    }

    get showWarningIcon(): boolean {
        return this.hasItemWithDisabledInvoicing ||
            (this.processorOptions.invoiceWarningMessage.includes("Please purchase separately") ?? false) ||
            (this.processorOptions.invoiceWarningMessage.includes("You must login") ?? false);
    }

    get isUserLoggedInFromMemberRegistration() {
        return this.userLoggedInGetter ? this.userLoggedInGetter() : false;
    }

    get isLoggedIn(): boolean {
        if (this.isUserLoggedInFromMemberRegistration) {
            this.currentUser.isLoggedIn = true; // this value is used the AchContainer to disable ACH for guest users
            return true;
        }
        return this.currentUser.isLoggedIn;
    }

    get showLoginRequired(): boolean {
        return this.processorOptions.invoiceRequiresLogin && !this.isLoggedIn;
    }

    login() {
        window.location.href = "/login?returnUrl=" + window.location.pathname;
    }

    onSubmit() {
        var validationPassed = false;

        this.$validator.validateAll()
            .then((valid: any) => {
                if (valid && !this.errors.any()) {
                    validationPassed = true;
                    this.onErrorToggle(false, '');
                }
                else {

                    if (!this.errors.any()) {
                        validationPassed = true;
                        this.onErrorToggle(false, '');
                    }
                    else {
                        validationPassed = false;
                        this.onErrorToggle(true, 'Please correct the errors shown before submitting.');
                    }
                }

                if (this.currentPaymentType == PaymentTypeEnum.creditCard || this.currentPaymentType == PaymentTypeEnum.paymentPlan) {
                    let creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;

                    if (this.total && this.total > 0 && creditCardContainer.isCheckingForBillableParties) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.billablePartiesNotLoadedErrorMessage);
                    }

                    if (this.total && this.total > 0 && this.currentPaymentType == PaymentTypeEnum.paymentPlan && creditCardContainer.isCheckingForPaymentPlans) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.paymentPlansNotLoadedErrorMessage);
                    }

                    if (this.total && this.total > 0 && this.paymentPlansActive && creditCardContainer.currentPlanId == 0) {
                        validationPassed = false;
                        this.onErrorToggle(true, "You must selected payment plan option to continue.");
                    }

                    if (this.total && this.total > 0 && this.paymentPlansActive && !creditCardContainer.acceptPaymentPlanTerms) {
                        validationPassed = false;
                        this.onErrorToggle(true, "You must accept payment plan conditions to continue.");
                    }
                }
                else if (this.currentPaymentType == PaymentTypeEnum.ach) {
                    let achContainer = <AchContainerClass>this.$refs.achContainer;

                    if (this.processorOptions.showAdminAchDisabledMessage) {
                        validationPassed = false;
                        this.onErrorToggle(true, "ACH is not available to admins.");
                    }
                    else if (this.total && this.total > 0 && achContainer.isCheckingForBillableParties) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.billablePartiesNotLoadedErrorMessage);
                    }
                }

                else if (this.currentPaymentType == PaymentTypeEnum.invoice) {
                    let invoiceContainer = <InvoiceContainerClass>this.$refs.invoice;

                    if (this.total && this.total > 0 && invoiceContainer.isCheckingForBillableParties) {
                        validationPassed = false;
                        this.onErrorToggle(true, this.billablePartiesNotLoadedErrorMessage);
                    }
                }
            })
            .then(() => {
                if (!validationPassed)
                    return;

                this.onProcessingUpdated(true);
                this.paymentContainerToken.paymentType = this.free ? PaymentTypeEnum.free : this.paymentType;

                if (this.paymentType === PaymentTypeEnum.invoice || this.free) {
                    this.tokenReady();
                }
                else if (this.paymentType == PaymentTypeEnum.creditCard || this.currentPaymentType == PaymentTypeEnum.paymentPlan) {
                    const creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
                    creditCardContainer.createPaymentToken();
                }
                else if (this.paymentType == PaymentTypeEnum.ach) {
                    const achContainer = <AchContainerClass>this.$refs.achContainer;
                    achContainer.createPaymentToken();
                }
            });
    }

    onErrorToggle(hasError: boolean, errorMsg: string) {
        if (!hasError) {
            this.hasError = false;
            this.errorMessage = '';
        }
        else {
            this.hasError = true;
            this.onProcessingUpdated(false);
            this.errorMessage = errorMsg ? errorMsg : 'An unspecified error has occurred. Please retry your current action. If this problem persists, please contact support.';
        }
    }

    handleGeneralError(message: string) {
        this.onErrorToggle(true, message);
    }

    onAllowSubmitToggle(allowSubmit: boolean) {
        this.creditCardLocked = !allowSubmit;
        this.invoiceLocked = !allowSubmit;
    }

    onForceSaveCardUpdated(forceSaveCard: boolean) {
        this.forceSaveCard = forceSaveCard;
    }

    onForceSaveAchUpdated(forceSaveAch: boolean) {
        this.forceSaveAch = forceSaveAch;
    }

    onCreditCardRestrictionUpdated(cardRestrictionMessage: string) {
        this.cardRestrictionMessage = cardRestrictionMessage;
    }

    onInvoiceWarningUpdated(message: string) {
        this.processorOptions.invoiceWarningMessage = message;
    }

    onTotalUpdated(total: number) {
        this.total = total;
    }

    onProposedDonateTransactionFeeAmountUpdated(value: number) {
        this.proposedDonateTransactionFeeAmount = value;
    }

    onEligibleForPaymentPlanAmountUpdated(value: number) {
        this.eligibleForPaymentPlanAmount = value;

        if (this.eligibleForPaymentPlanAmount < this.paymentPlanMinimumAmount && this.paymentPlansActive) {
            this.paymentPlansActive = false;
            this.creditCardActive = true;
        }
    }

    onEligibleItemsForPaymentPlanUpdated(value: []) {
        this.eligibleItemsForPaymentPlan = value;

        if (this.eligibleItemsForPaymentPlan.length == 0 && this.paymentPlansActive) {
            this.paymentPlansActive = false;
            this.creditCardActive = true;
        }
    }

    onEnableAutoPayUpdated(enableAutoPay: boolean) {
        this.processorOptions.enableAutoPay = enableAutoPay;
    }

    onProcessingUpdated(processing: boolean) {
        this.isProcessing = processing;
        this.$emit("update:is-processing", this.isProcessing);
    }

    onCreditCardBillingStateUpdate(state: string) {
        this.$emit("update:credit-card-billing-state", state);
    }

    onIsDonatingTransactionFeeUpdated(value: boolean) {
        this.$emit("update-is-donating-transaction-fee", value);
        this.isDonatingTransactionFee = value;
    }

    onPurchaserChanged(value: string) {
        this.processorOptions.billablePartyId = parseInt(value, 10);
    }

    @Watch('convenienceFee')
    onConvenienceFeeChanged(newConvenienceFee: boolean) {
        this.$emit("update:convenience-fee", newConvenienceFee);
    }

    @Watch('paymentType')
    onPaymentTypeChanged(newPaymentType: PaymentTypeEnum) {
        this.$emit("update:payment-type", newPaymentType.toString());
        this.$nextTick(() => {
            if (newPaymentType.toString() == 'ACH') {
                const achContainer = <AchContainerClass>this.$refs.achContainer;
                if (achContainer) {
                    if (this.guestName) {
                        achContainer.onGuestDataUpdated(this.guestName, true);
                    }
                    if (this.guestCompany) {
                        achContainer.onGuestDataUpdated(this.guestCompany, false);
                    }
                }
            }
            if (newPaymentType.toString() == 'credit-card') {
                const creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
                if (creditCardContainer) {
                    if (this.guestName) {
                        creditCardContainer.onGuestDataUpdated(this.guestName, true);
                    }
                    if (this.guestCompany) {
                        creditCardContainer.onGuestDataUpdated(this.guestCompany, false);
                    }
                }
            }
        });
    }


    @Watch('errorMessage')
    onErrorMessageChanged(newErrorMessage: boolean) {
        this.$emit("update:error-message", newErrorMessage);
    }

    @Watch('free')
    onFreeChange(newFree: boolean) {
        if (newFree) {
            //If we are going free, switch to next available payment method so no invoicing blocking rules affect checkout

            if (this.creditCardActive) {
                this.toggleTabActivation(PaymentTypeEnum.creditCard);
            }
            else if (this.achActive) {
                this.toggleTabActivation(PaymentTypeEnum.ach);
            }

            if (this.paymentPlansActive) {
                this.toggleTabActivation(PaymentTypeEnum.paymentPlan);
            }
            else {
                this.toggleTabActivation(PaymentTypeEnum.creditCard); // fallback
            }
        }
    }

    handleApiPaymentError(paymentResponse: ApiPaymentErrorResponse) {
        this.onErrorToggle(true, paymentResponse.message);
        switch (paymentResponse.paymentProcessingStatus) {
            case PaymentProcessingStatus.Success:
            case PaymentProcessingStatus.Error:
                //Success: Payment was successful, but we caught an error so something else went wrong and we can't retry
                //Error: Payment had an error, we don't know the status and we can't retry
                this.creditCardLocked = true;
                this.invoiceLocked = true;
                break;
            case PaymentProcessingStatus.Declined:
                //Declined: Payment was declined/not processed for the given reason, need to fix and retry
                this.resetIdempotencyKey();
                break;
            case PaymentProcessingStatus.None:
                //None: We hit an error before payment was attempted - fully safe to retry                
                break;
        }
    }

    onInvoiceAllowedUpdated(invoiceAllowed: boolean) {
        this.localEnableInvoice = invoiceAllowed;
    }

    onIsCreditCardProcessingPreventedUpdated(value: boolean) {
        this.preventCreditCard = value;
    }

    onIsAchProcessingPreventedUpdated(value: boolean) {
        this.preventAch = value;
    }

    onInvoiceTokenUpdated(invoiceToken: InvoiceTokenData) {
        var taxableChanged = invoiceToken.taxable != this.paymentContainerToken.invoiceTokenData.taxable;
        //console.log(`Invoice Token Updated: (Billable Party: ${invoiceToken.billablePartyId}) (Sales Term: ${invoiceToken.salesTermId}) (Waive Invoice Fee: ${invoiceToken.waiveInvoiceFee})`);
        Object.assign(this.paymentContainerToken.invoiceTokenData, invoiceToken);

        if (taxableChanged) {
            this.$emit("update:billable-party-taxable", invoiceToken.taxable);
        }
    }

    onCreditCardTokenUpdated(creditCardToken: CreditCardTokenData) {
        Object.assign(this.paymentContainerToken.creditCardTokenData, creditCardToken);
    }

    onAchTokenUpdated(achToken: AchTokenData) {
        Object.assign(this.paymentContainerToken.achTokenData, achToken);
    }

    onPaymentPlansActiveUpdated(value: boolean) {
        this.paymentPlansActive = value;
    }

    onPaymentPlansUpdated(value: []) {
        this.paymentPlans = value;
    }


    onCreditCardPaymentTokenReady(cardToken: CreditCardTokenData) {
        //console.log("Credit card token received by payment container: " + cardToken.cardToken);
        this.paymentContainerToken.paymentType = PaymentTypeEnum.creditCard;

        // If the credit card details changed since the last submission, then we need to send a new idempotency key
        // Note: this is really just for stripe, which returns an error if you re-submit a payment with the same idempotency key but different card details
        // QB Payments ignores the payment details if you re-submit with the same idempotency key and returns whatever response it gave the last time
        if (this.paymentContainerToken.creditCardTokenData.storedCardId != cardToken.storedCardId
            || this.paymentContainerToken.creditCardTokenData.cardToken != cardToken.cardToken
            || this.paymentContainerToken.creditCardTokenData.confirmationToken != cardToken.confirmationToken) {
            this.resetIdempotencyKey();
        }

        Object.assign(this.paymentContainerToken.creditCardTokenData, cardToken);
        this.tokenReady();
    }

    onAchPaymentTokenReady(cardToken: AchTokenData) {
        this.paymentContainerToken.paymentType = PaymentTypeEnum.ach;

        Object.assign(this.paymentContainerToken.achTokenData, cardToken);
        this.tokenReady();
    }

    toggleTabActivation(paymentType: PaymentTypeEnum) {
        this.creditCardActive = paymentType === PaymentTypeEnum.creditCard || paymentType == PaymentTypeEnum.free;
        this.invoiceActive = paymentType === PaymentTypeEnum.invoice;
        this.achActive = paymentType === PaymentTypeEnum.ach;
        this.paymentPlansActive = paymentType == PaymentTypeEnum.paymentPlan;
        this.paymentType = paymentType;
    }

    @Emit('payment-token-ready')
    tokenReady() {
        // When the token is ready we need set all the auto pay options to be mapped to it to mark it as enabled. 
        // If its a new card we won't have the store ID so just pass in the constant NEW-CARD-ACCOUNT and the backend code will know to take the new id and set it there. 
        if (this.autoPayOptions != null) {
            if (this.paymentContainerToken.paymentType == PaymentTypeEnum.creditCard || this.paymentContainerToken.paymentType == PaymentTypeEnum.ach) {

                var externalPaymentMethodId = this.paymentContainerToken.paymentType == PaymentTypeEnum.creditCard
                    ? (this.paymentContainerToken.creditCardTokenData.storedCardId || "NEW-CARD-ACCOUNT")
                    : (this.paymentContainerToken.achTokenData.storedBankAccountId || "NEW-CARD-ACCOUNT");

                this.autoPayOptions.forEach(x => {
                    if (x.enabled) {
                        x.externalPaymentMethodId = externalPaymentMethodId;
                    }
                })

                if (this.paymentContainerToken.paymentType == PaymentTypeEnum.creditCard) {
                    this.paymentContainerToken.creditCardTokenData.autoPayOptionsJson = JSON.stringify(this.autoPayOptions);
                }
                else if (this.paymentContainerToken.paymentType == PaymentTypeEnum.ach) {
                    this.paymentContainerToken.achTokenData.autoPayOptionsJson = JSON.stringify(this.autoPayOptions);
                }
            }
        }

        return this.paymentContainerToken;
    }

    // Borrowed from https://stackoverflow.com/a/2117523/215141
    uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    resetIdempotencyKey() {
        this.paymentContainerToken.idempotencyKey = this.uuidv4();
    }

    created() {
        EventBus.$on('api-payment-error', this.handleApiPaymentError);
        this.loadCurrentUser();
    }

    mounted() {
        this.selectDefaultPaymentTab();
        this.isProcessing = false;
        this.hasItemWithDisabledInvoicing = this.processorOptions.hasItemWithDisabledInvoicing;
        this.disabledInvoiceTitle = this.hasItemWithDisabledInvoicing ? 'One or more attendee tickets has invoicing disabled.' : ''
        this.resetIdempotencyKey();

        if (this.processorOptions.enableAutoPay) {
            this.getAutoPayOptions();
        }

        if (this.initialErrorMessage) {
            this.onErrorToggle(true, this.initialErrorMessage)
        }
    }

    selectDefaultPaymentTab() {
        // Default states
        this.creditCardActive = false;
        this.achActive = false;
        this.invoiceActive = false;
        this.paymentPlansActive = false;

        const defaultCreditCardExists = this.processorOptions.enableCreditCard &&
            this.processorOptions.savedCards &&
            this.processorOptions.savedCards.some(card => card.default);

        const defaultAchExists = this.processorOptions.enableACH &&
            this.processorOptions.savedAchs &&
            this.processorOptions.savedAchs.some(ach => ach.default);

        if (defaultCreditCardExists) {
            this.creditCardActive = true;
        } else if (defaultAchExists) {
            this.achActive = true;
        } else if (this.processorOptions.enableCreditCard) {
            this.creditCardActive = true;
        } else if (this.processorOptions.enableACH) {
            this.achActive = true;
        } else if (this.processorOptions.enableInvoice) {
            this.invoiceActive = true;
        } else {
            this.creditCardActive = true;
        }

        // Handle payment type setting on page load
        if (this.achActive) {
            this.toggleTabActivation(PaymentTypeEnum.ach);
        }
        else if (this.creditCardActive) {
            this.toggleTabActivation(PaymentTypeEnum.creditCard);
        }
        else if (this.invoiceActive) {
            this.toggleTabActivation(PaymentTypeEnum.invoice);
        }
    }

    onGuestNameUpdated(name: string) {
        if (name) {
            this.guestName = name;
            let creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
            if (creditCardContainer) {
                creditCardContainer.onGuestDataUpdated(name, true);
            }
            let achContainer = <AchContainerClass>this.$refs.achContainer;
            if (achContainer) {
                achContainer.onGuestDataUpdated(name, true);
            }
        }
    }

    onGuestCompanyUpdated(company: string) {
        if (company) {
            this.guestCompany = company;
            let creditCardContainer = <CreditCardContainerClass>this.$refs.creditCardContainer;
            if (creditCardContainer) {
                creditCardContainer.onGuestDataUpdated(company, false);
            }
            let achContainer = <AchContainerClass>this.$refs.achContainer;
            if (achContainer) {
                achContainer.onGuestDataUpdated(company, false);
            }
        }
    }

    getAutoPayOptions() {
        if (this.autoPayOptionsPulled) {
            return;
        }

        var type = this.processorOptions.isPayingSubscriptionInvoice ? 'subscription' : 'membership';

        paymentsRepo.getAutoPayOptions(type).then((result: { items: AutoPayOption[] }) => {
            this.autoPayOptions = result.items;
            this.autoPayOptionsPulled = true;

            // If auto pay is required for this member then auto enable the checkbox
            result.items.forEach(x => {
                x.enabled = x.forceAutoPay || x.enabled;
            });
        }).catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => { });
    }
    
    private loadCurrentUser() {
        customerRepo.getCurrentUser()
            .then(user => {
                Object.assign(this.currentUser, user);
            })
            .catch(error => {
                console.error("Unable to load current user.", error);
            })
            .finally(() => {
                this.userLoaded = true;
            });
    }
}